import type { ComputedColumnPlacement, SectionBoundary } from "building-types/columns/columnBaseTypes";
import type {
  RelativeDatumTable,
  RelativeVerticalDatumData,
  VerticalDatumData,
} from "building-types/columns/columnDatumTypes";
import type { StructuralColumnDescriptor } from "building-types/columns/columnDescriptorTypes";
import type { GeneratedColumn } from "building-types/columns/columnGeneratedTypes";
import type { ColumnSection, ColumnV2 } from "building-types/columns/columnTypes";
import type { ColumnHorizontalAlignment, ColumnLateralAlignment } from "building-types/columns/columnTypesEnum";
import {
  ColumnPlacementType,
  StructuralColumnBuilt,
  StructuralColumnMaterial,
} from "building-types/columns/columnTypesEnum";
import type { ResolvedDatumTable, WallLateralDatumAnchorAll } from "building-types/datums/datumsTypes";
import type { WallHorizontalDatum } from "building-types/datums/datumsTypesEnum";
import type { HorizontalEdgePlacementNoExpr } from "building-types/entities/edgePlacementTypes";
import type { ColumnHandle } from "building-types/handles/docEntityHandleTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { ExteriorMaterial } from "building-types/materials/exteriorMaterialTypes";
import type { RoomIndex } from "building-types/rooms/roomIndex";
import type { RoomOutlines } from "building-types/rooms/roomOutlineTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { WallMassSystem } from "building-types/walls/wallSytemTypes";
import type { RWTable, Table } from "common/data/table";
import type { WorldInches } from "common/types/nominalNumbersTypes";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { worldInchesNew } from "common/utils/nominalNumbers";
import type { WallLayerExtentsCache } from "doc/elements/openings/openingWallLayersTypes";
import { entityParamFromSpecificWallLayer } from "doc/elements/openings/openingWallLayersUtils";
import { generatedColumnNew } from "doc/utils/columnGeneratedUtils";
import { resolveAlignmentsToShifts } from "doc/utils/columnSharedUtils";
import { getRelativeDatumHeight } from "doc/utils/datumsUtils";
import { aabb2ByPolyLine2s } from "geom/aabb2";
import type { AABB2 } from "geom/aabb2Types";
import type { Extrusion } from "geom/brep";
import { extrudePolyLine2InZ } from "geom/extrude";
import {
  polyLine2Copy,
  polyLine2Edge,
  polyLine2EdgeEvalOffset,
  polyLine2Move,
  polyLine2Reverse,
  polyLine2Rotate,
  projectPolyLine2,
} from "geom/polyLine2";
import type { LineAnchor, PolyLine2 } from "geom/polyLine2Types";
import { rangeInclusiveByMinStep } from "geom/range";
import { rect2ByTwoPoints } from "geom/rect2";
import {
  edgeLengthFromSpecificWallLayer,
  entityLateralOffsetFromWallDatum,
  getFromOrAddToWallLayerExtentsCache,
} from "../../elements/openings/openingWallLayers";
import { isCombinedGlobalInstanceMirror } from "../../generationTools/generateOpeningsTools";

/**
 *
 * @param roomEdge roomEdge to be projected
 * @param datumEdge projection target
 * @returns projected line
 */
export function projectRoomEdgeOnDatumEdge(roomEdge: PolyLine2, datumEdge: PolyLine2): PolyLine2 {
  const projected = projectPolyLine2(datumEdge, roomEdge);

  return projected ?? roomEdge;
}

/**
 *
 * @param sections column sections
 * @returns the section boundaries
 */
export function generateSectionBoundaryArray(sections: ColumnSection[]): SectionBoundary[] {
  const sectionBoundaries: SectionBoundary[] = [];
  for (const section of sections) {
    const { width, height, depth, bottomElevation } = section;
    if (bottomElevation === undefined) {
      // added this for strict null check, since bottom elevation is options in section but not in section boundaries
      continue;
    }
    const sectionBoundary = rect2ByTwoPoints(-0.5 * height, -0.5 * width, 0.5 * height, 0.5 * width);
    sectionBoundaries.push({ boundary: sectionBoundary, bottomElevation, depth });
  }
  return sectionBoundaries;
}

/**
 *
 * @param computedColumnPlacementArray the array of computed columns to add the column center positions to
 * @param sectionBoundaries section boundaries of the column
 * @param edgeId  the edge id to resolve the column placement on
 * @param room  the room the column is in
 * @param material  the material of the column
 * @param mirror  whether the column is mirrored
 * @param columns  the columns table to add the generated columns to
 * @param handle  the column handle
 * @param pickIdRegistry
 */
export function generateColumnsAtComputedPlacements(
  computedColumnPlacementArray: ComputedColumnPlacement[],
  sectionBoundaries: SectionBoundary[],
  edgeId: string,
  room: Room,
  material: ExteriorMaterial,
  mirror: boolean,
  columns: RWTable<GeneratedColumn>,
  handle: ColumnHandle,
) {
  const effectiveMirror = isCombinedGlobalInstanceMirror(mirror, room);
  const edgeIdx = room.edgeIds.indexOf(edgeId);
  const edge = polyLine2Edge(room.outline, edgeIdx);
  if (effectiveMirror) {
    polyLine2Reverse(edge);
  }
  const edgeAngle = Math.atan2(edge[3] - edge[1], edge[2] - edge[0]);

  for (const computedColumn of computedColumnPlacementArray) {
    const { position: vec2d, isolatedAssemblyId } = computedColumn;
    const planProfiles: PolyLine2[] = [];
    const extrusions: Extrusion[] = [];

    //loop over all the sections
    for (const section of sectionBoundaries) {
      const { boundary: sectionBoundary, depth, bottomElevation } = section;
      const sectionBoundaryMod = polyLine2Copy(sectionBoundary);
      polyLine2Rotate(sectionBoundaryMod, edgeAngle, 0, 0);
      const planProfile = polyLine2Move(sectionBoundaryMod, vec2d[0], vec2d[1]);
      planProfiles.push(planProfile);
      extrusions.push(extrudePolyLine2InZ(planProfile, bottomElevation, depth));
    }
    // A column inside a component instance is selected as the instance, not as itself.
    const columnPickHandle: DocEntityHandle = room.instanceId
      ? { type: "COMPONENT_INSTANCE", id: room.instanceId }
      : handle;

    const col = generatedColumnNew(
      room.level.id,
      room.id,
      edgeId,
      planProfiles,
      extrusions,
      material,
      handle,
      columnPickHandle,
      isolatedAssemblyId,
    );
    columns[col.id] = col;
  }
}

/**
 * @param computedColumnPlacementArray the array of computed columns to add the column center positions to
 * @param sectionBoundaryBottom  the bottom section boundary of the column
 */
export function resolveColumnPlacementV2(
  room: Room,
  column: ColumnV2,
  mirror: boolean,
  computedColumnPlacementArray: ComputedColumnPlacement[],
  roomIndex: RoomIndex,
  wallLayerExtentsCache: WallLayerExtentsCache,
  wallMasses: Table<WallMassSystem>,
  roomOutlines: RoomOutlines,
  sectionBoundaryBottom: SectionBoundary,
) {
  const effectiveMirror = isCombinedGlobalInstanceMirror(mirror, room);
  const {
    edgeId,
    param: p,
    paramNormalized: pN,
    paramAnchor: pA,
    placement: columnPlacement,
    horizontalDatum,
    horizontalAlignment,
    lateralDatum,
    lateralOffset,
    lateralOffsetNormalized,
    lateralAlignment,
    isolatedAssemblyId,
  } = column;

  const wallMassSystem = wallMasses[room.level.id];

  const edgeIndex = room.edgeIds.indexOf(edgeId);
  const edge = polyLine2Edge(room.outline, edgeIndex);
  if (!edge) {
    return null;
  }

  const { edgeMinTable, edgeMaxTable, largestInsideOffset, mostInwardLayerIntoRoom, roomEdgeLength } =
    getFromOrAddToWallLayerExtentsCache(room, edgeIndex, wallLayerExtentsCache, roomIndex, wallMassSystem);

  const edgeLength = edgeLengthFromSpecificWallLayer({
    placement: { edgeId, horizontalDatum },
    largestOffset: largestInsideOffset,
    edgeLength: roomEdgeLength,
    mirror: effectiveMirror,
    mostInwardLayerIntoRoom,
    edgeMinTable,
    edgeMaxTable,
  });

  const { boundary } = sectionBoundaryBottom;
  const aabb = aabb2ByPolyLine2s([boundary]);
  if (!aabb) {
    return undefined;
  }

  //this is the base section of the column which is used to determine the column's center position wrt the edge

  switch (columnPlacement.type) {
    case ColumnPlacementType.PARAM: {
      const position = getObjectPlacementPositionV2(
        wallMasses,
        wallLayerExtentsCache,
        room,
        effectiveMirror,
        edgeId,
        roomIndex,
        p,
        pN,
        pA,
        horizontalDatum,
        horizontalAlignment,
        lateralOffsetNormalized,
        lateralOffset,
        lateralAlignment,
        lateralDatum,
        roomOutlines,
        aabb,
      );
      if (!position) {
        return undefined;
      }
      const [x, y] = position;
      computedColumnPlacementArray.push({
        position: [x, y],
        isolatedAssemblyId,
      });
      break;
    }
    case ColumnPlacementType.ARRAY: {
      const { spacing, shift, maxCount: limit } = columnPlacement;
      if (spacing > 0) {
        const spaceToFill = edgeLength - shift;
        const calcCount = Math.ceil(spaceToFill / spacing);
        const count = limit !== undefined ? Math.min(limit, calcCount) : calcCount;
        for (let j = 0; j < count; j++) {
          const param = shift + spacing * j;
          if (param >= 0 && param <= edgeLength) {
            const paramAdjustment = pN ? param / edgeLength : param;
            const position = getObjectPlacementPositionV2(
              wallMasses,
              wallLayerExtentsCache,
              room,
              effectiveMirror,
              edgeId,
              roomIndex,
              p + paramAdjustment,
              pN,
              pA,
              horizontalDatum,
              horizontalAlignment,
              lateralOffsetNormalized,
              lateralOffset,
              lateralAlignment,
              lateralDatum,
              roomOutlines,
              aabb,
            );
            if (!position) {
              return undefined;
            }
            const [x, y] = position;
            computedColumnPlacementArray.push({
              position: [x, y],
              isolatedAssemblyId,
            });
          }
        }
      }
      break;
    }
    case ColumnPlacementType.DIVISION: {
      const { segments } = columnPlacement;
      if (segments > 0) {
        const fact = 1 / segments;
        for (let j = 0; j <= segments; j++) {
          const paramAdjustment = pN ? fact * j : fact * j * edgeLength;

          const position = getObjectPlacementPositionV2(
            wallMasses,
            wallLayerExtentsCache,
            room,
            effectiveMirror,
            edgeId,
            roomIndex,
            p + paramAdjustment,
            pN,
            pA,
            horizontalDatum,
            horizontalAlignment,
            lateralOffsetNormalized,
            lateralOffset,
            lateralAlignment,
            lateralDatum,
            roomOutlines,
            aabb,
          );
          if (!position) {
            return undefined;
          }
          const [x, y] = position;
          computedColumnPlacementArray.push({
            position: [x, y],
            isolatedAssemblyId,
          });
        }
      }
      break;
    }
    case ColumnPlacementType.MAX_DISTANCE: {
      const { maxDistance } = columnPlacement;
      const steps = rangeInclusiveByMinStep(0, edgeLength, maxDistance);
      for (const stepIndex of Object.keys(steps)) {
        const param = steps[stepIndex];
        if (param >= 0 && param <= edgeLength) {
          const paramAdjustment = pN ? param / edgeLength : param;
          const position = getObjectPlacementPositionV2(
            wallMasses,
            wallLayerExtentsCache,
            room,
            effectiveMirror,
            edgeId,
            roomIndex,
            p + paramAdjustment,
            pN,
            pA,
            horizontalDatum,
            horizontalAlignment,
            lateralOffsetNormalized,
            lateralOffset,
            lateralAlignment,
            lateralDatum,
            roomOutlines,
            aabb,
          );
          if (!position) {
            return undefined;
          }
          const [x, y] = position;
          computedColumnPlacementArray.push({
            position: [x, y],
            isolatedAssemblyId,
          });
        }
      }
      break;
    }
    default:
      assertUnreachable(columnPlacement);
  }
  return undefined;
}

/**
 * The params are explained in their respective files and are standardized across moveable entities
 * @returns the final position of the object
 */
function getObjectPlacementPositionV2(
  wallMasses: Table<WallMassSystem>,
  wallLayerExtentsCache: WallLayerExtentsCache,
  room: Room,
  mirror: boolean,
  edgeId: string,
  roomIndex: RoomIndex,
  paramInitial: number,
  paramNormalized: boolean,
  paramAnchor: LineAnchor,
  horizontalDatum: WallHorizontalDatum,
  horizontalAlignment: ColumnHorizontalAlignment,
  lateralOffsetNormalized: boolean,
  lateralOffset: number,
  lateralAlignment: ColumnLateralAlignment,
  lateralDatum: WallLateralDatumAnchorAll,
  roomOutlines: RoomOutlines,
  aabbObject: AABB2,
) {
  const wallMassSystem = wallMasses[room.level.id];

  const edgeIndex = room.edgeIds.indexOf(edgeId);
  const edge = polyLine2Edge(room.outline, edgeIndex);
  if (!edge) {
    return null;
  }

  const { horizontalShift, lateralShift } = resolveAlignmentsToShifts(
    aabbObject,
    horizontalAlignment,
    lateralAlignment,
  );

  const { edgeMinTable, edgeMaxTable, largestInsideOffset, mostInwardLayerIntoRoom, roomEdgeLength } =
    getFromOrAddToWallLayerExtentsCache(room, edgeIndex, wallLayerExtentsCache, roomIndex, wallMassSystem);

  const horizontalEdgePlacement: HorizontalEdgePlacementNoExpr = {
    edgeId,
    param: paramInitial,
    paramNormalized,
    paramAnchor,
    horizontalDatum,
  };

  const { param } = entityParamFromSpecificWallLayer({
    placement: horizontalEdgePlacement,
    largestOffset: largestInsideOffset,
    edgeLength: roomEdgeLength,
    mirror,
    mostInwardLayerIntoRoom,
    edgeMinTable,
    edgeMaxTable,
  });

  const resolvedLateralOffset = entityLateralOffsetFromWallDatum({
    room,
    edgeIndex,
    roomOutlines,
    wallMassSystem,
    placement: {
      lateralDatum: lateralDatum,
      lateralOffsetNormalized,
      lateralOffset,
    },
    resolvedParam: param,
  });

  const [x, y] = polyLine2EdgeEvalOffset(
    edge,
    0,
    -(resolvedLateralOffset + lateralShift),
    param + (mirror ? -horizontalShift : horizontalShift),
  );
  return [x, y];
}

/**
 *
 * @param datum RelativeVerticalDatumData
 * @param levels levels table
 * @param host This is the room for which the relative datum is being resolved
 * @returns
 */
export function resolveRelativeVerticalDatumForTopAndBottom(
  datum: RelativeVerticalDatumData,
  levels: Table<Level>,
  host: Room,
): RelativeDatumTable {
  const relativeDatumTopAndBottom: RelativeDatumTable = {};
  const datums = [datum.topDatumId, datum.bottomDatumId];

  datums.forEach((datumId) => {
    if (!datumId) {
      return;
    }
    relativeDatumTopAndBottom[datumId] = getRelativeDatumHeight(datumId, host.level.id, levels);
  });

  return relativeDatumTopAndBottom;
}

//this function is used to compute the column sections for the structural column
//currently it only supports wood posts and may need to be updated to support other materials
//currently porches are not supporting structural columns but this function may be used to compute the sections for porches in the future
export function computeStructuralColumnSections(
  column: StructuralColumnDescriptor,
  resolvedDatums?: ResolvedDatumTable,
  relativeDatums?: RelativeDatumTable,
  calculateDatumZ?: (
    datumId: keyof Pick<VerticalDatumData, "topDatumId" | "bottomDatumId">,
    datum: VerticalDatumData,
    resolvedDatums?: ResolvedDatumTable,
    relativeDatums?: RelativeDatumTable,
  ) => WorldInches,
  verticalDatumOverrides?: VerticalDatumData,
): ColumnSection[] {
  const verticalDatum = verticalDatumOverrides ?? column.verticalDatum;
  const topDatumZ = calculateDatumZ ? calculateDatumZ("topDatumId", verticalDatum, resolvedDatums, relativeDatums) : 0;
  const bottomDatumZ = calculateDatumZ
    ? calculateDatumZ("bottomDatumId", verticalDatum, resolvedDatums, relativeDatums)
    : 0;

  const sections: ColumnSection[] = [];
  let depth = topDatumZ - bottomDatumZ;
  depth = depth < 0 ? 0 : depth;

  switch (column.columnMaterial) {
    case StructuralColumnMaterial.WOOD: {
      switch (column.built) {
        case StructuralColumnBuilt.POST: {
          sections.push({
            height: column.postThickness,
            width: column.postWidth,
            depth,
            paletteId: column.paletteId,
            taper: false,
            bottomElevation: worldInchesNew(bottomDatumZ),
          });
          break;
        }
        default:
          assertUnreachable(column.built);
      }
      break;
    }
    default:
      assertUnreachable(column.columnMaterial);
  }
  return sections;
}
